import { useState } from 'react'
import { ArrowLeft, Grid3x3, LayoutList, Star, Users } from 'lucide-react'
import { useMarketSegmentation } from '@/hooks/useMarketSegmentation'
import { SegmentsTab } from './tabs/SegmentsTab'
import { OverviewTab } from './tabs/OverviewTab'
import { PersonasTab } from './tabs/PersonasTab'

type Tab = 'overview' | 'segments' | 'personas'

interface Props {
  accountId: string
  projectId: string
}

export function MarketSegmentationPage({ accountId, projectId }: Props) {
  const state = useMarketSegmentation(accountId, projectId)
  const [activeTab, setActiveTab] = useState<Tab>('segments')
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null)
  const [fullScreen, setFullScreen] = useState(false)

  const beachhead = state.canvas?.beachheadSegmentId
    ? state.segments.find((s) => s.id === state.canvas?.beachheadSegmentId)
    : null

  const openSegment = (id: string) => {
    setSelectedSegmentId(id)
    setActiveTab('segments')
  }

  const toggleFullScreen = () => {
    if (!selectedSegmentId) return
    setFullScreen((prev) => !prev)
  }

  const exitFullScreen = () => setFullScreen(false)

  // Fullscreen mode replaces the entire module chrome (tabs/header) with a
  // compact "back to segments" bar so the segment detail can use all available
  // vertical space.
  if (fullScreen && selectedSegmentId) {
    return (
      <div
        className="flex h-full flex-col"
        style={{ backgroundColor: 'var(--color-bg, white)' }}
      >
        <div
          className="flex shrink-0 items-center justify-between border-b px-6 py-3"
          style={{
            borderColor: 'var(--color-border-subtle)',
            backgroundColor: 'var(--color-warm-50)',
          }}
        >
          <button
            type="button"
            onClick={exitFullScreen}
            className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors hover:bg-white"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to segments
          </button>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Segment detail · full screen
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <SegmentsTab
            state={state}
            selectedId={selectedSegmentId}
            onSelectedChange={setSelectedSegmentId}
            fullScreen
            onToggleFullScreen={toggleFullScreen}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex h-full flex-col"
      style={{ backgroundColor: 'var(--color-warm-50)' }}
    >
      <header
        className="shrink-0 border-b px-8 pt-6 pb-0"
        style={{
          borderColor: 'var(--color-border-subtle)',
          backgroundColor: 'var(--color-bg, white)',
        }}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold text-[var(--color-text)]">
                Market segmentation
              </h1>
              {beachhead ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                  style={{
                    backgroundColor: '#eaf6ff',
                    color: 'var(--color-business, #0984e3)',
                  }}
                >
                  <Star className="h-3 w-3" />
                  Beachhead: {beachhead.name}
                </span>
              ) : null}
            </div>
            <p
              className="mt-1.5 max-w-2xl text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Rank segments by evidence — size, scoring, and buyer structure grounded in your
              project knowledge graph.
            </p>
          </div>
        </div>

        <nav
          className="mt-4 flex items-end gap-8 border-b"
          style={{ borderColor: 'transparent', marginBottom: -1 }}
        >
          <TabButton
            icon={<Grid3x3 className="h-3.5 w-3.5" />}
            label="Overview"
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          />
          <TabButton
            icon={<LayoutList className="h-3.5 w-3.5" />}
            label="Segments"
            count={state.segments.length}
            active={activeTab === 'segments'}
            onClick={() => setActiveTab('segments')}
          />
          <TabButton
            icon={<Users className="h-3.5 w-3.5" />}
            label="Personas"
            count={state.personas.length}
            active={activeTab === 'personas'}
            onClick={() => setActiveTab('personas')}
          />
        </nav>
      </header>

      <div
        className="min-h-0 flex-1 overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg, white)' }}
      >
        {activeTab === 'overview' ? (
          <OverviewTab state={state} onOpenSegment={openSegment} />
        ) : null}
        {activeTab === 'segments' ? (
          <SegmentsTab
            state={state}
            selectedId={selectedSegmentId}
            onSelectedChange={setSelectedSegmentId}
            onToggleFullScreen={toggleFullScreen}
          />
        ) : null}
        {activeTab === 'personas' ? (
          <PersonasTab accountId={accountId} projectId={projectId} state={state} />
        ) : null}
      </div>
    </div>
  )
}

interface TabButtonProps {
  icon: React.ReactNode
  label: string
  count?: number
  active: boolean
  onClick: () => void
}

function TabButton({ icon, label, count, active, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 pb-3 pt-1 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        borderBottom: active ? '2px solid var(--color-text)' : '2px solid transparent',
      }}
    >
      {icon}
      <span>{label}</span>
      {typeof count === 'number' ? (
        <span
          className="inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold"
          style={{
            backgroundColor: active ? 'var(--color-text)' : 'var(--color-warm-200)',
            color: active ? 'white' : 'var(--color-text-muted)',
          }}
        >
          {count}
        </span>
      ) : null}
    </button>
  )
}
